'use client'

import { ConsentBanner, ConsentDialog, ConsentManagerProvider } from '@c15t/nextjs'
import { googleTagManager } from '@c15t/scripts/google-tag-manager'
import { baseTranslations } from '@c15t/translations/all'
import type { ReactNode } from 'react'

const BANNER_TITLE = 'Wir verwenden Cookies'
const BANNER_DESCRIPTION =
  'Wir verwenden Cookies, um Ihnen die beste Erfahrung zu bieten. Durch die Nutzung unserer Website erklären Sie sich mit der Verwendung von Cookies einverstanden.'

// c15t only bundles English by default, so the German base strings (category
// names, button labels) have to be supplied explicitly.
const de = baseTranslations.de

export function ConsentManagerClient({ children }: { children: ReactNode }) {
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID || ''

  return (
    <ConsentManagerProvider
      options={{
        mode: 'offline',
        consentCategories: ['necessary', 'marketing', 'experience', 'measurement'],
        // Force the GDPR jurisdiction so the banner is shown to every visitor,
        // regardless of where they are browsing from.
        overrides: {
          country: 'DE',
        },
        scripts: [
          googleTagManager({
            id: gtmId,
          }),
        ],
        i18n: {
          locale: 'de',
          messages: {
            de: {
              ...de,
              cookieBanner: {
                ...de.cookieBanner,
                title: BANNER_TITLE,
                description: BANNER_DESCRIPTION,
              },
              consentManagerDialog: {
                ...de.consentManagerDialog,
                title: BANNER_TITLE,
                description: BANNER_DESCRIPTION,
              },
              common: {
                ...de.common,
                acceptAll: 'Akzeptieren',
                rejectAll: 'Ablehnen',
                customize: 'Anpassen',
              },
            },
          },
        },
        scrollLock: true,
        theme: {
          slots: {
            consentBanner: 'flex justify-center items-center !w-screen !h-screen',
            consentBannerCard: 'bg-white rounded-lg shadow-lg max-w-4xl mx-auto',
            consentBannerHeader: 'flex flex-col items-center text-center mb-4',
            consentBannerTitle: 'text-xl font-bold mb-2',
            consentBannerDescription: 'text-sm text-gray-600 mb-4',
            consentBannerFooter: 'flex justify-center items-center gap-4 flex-wrap',
            consentBannerFooterSubGroup: 'flex items-center gap-3',
            consentBannerOverlay: 'bg-black/50 backdrop-blur-sm',
          },
        },
      }}
    >
      <ConsentBanner />
      <ConsentDialog />
      {children}
    </ConsentManagerProvider>
  )
}
